import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { registerDestructor } from '@ember/destroyable';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import { not } from 'tio-ui/utilities';
import Drawer from 'tio-ui/components/drawer';
import Button from 'tio-ui/components/button';
import Text from 'tio-ui/components/text';
import FileUploader from 'tio-common/components/file-uploader';
import { presentationForSlrLoanStanding } from 'tio-employee/utils/slr-loan-standing-verification';
import { PhCheckCircle, PhFile, PhInfo, PhWarning, PhXCircle } from 'ember-phosphor-icons';
import type Owner from '@ember/owner';
import type CableService from 'tio-common/services/cable';
import type SlrLoanStandingVerificationService from 'tio-employee/services/slr-loan-standing-verification';
import type { DigitalAssetDetailSignature } from 'tio-common/services/digital-assets';
import type {
  SlrLoanStandingAuditState,
  SlrLoanStandingCablePayload,
} from 'tio-employee/types/slr-loan-standing-verification';

type DrawerState = 'idle' | 'file-selected' | 'processing' | 'success' | 'error';

interface SelectedFile {
  name: string;
  details: DigitalAssetDetailSignature;
}

interface LoanStandingUploadDrawerSignature {
  Args: {
    isOpen: boolean;
    state: SlrLoanStandingAuditState;
    onClose: () => void;
    onRefresh: () => Promise<void> | void;
  };
  Element: HTMLDivElement;
}

export default class LoanStandingUploadDrawer extends Component<LoanStandingUploadDrawerSignature> {
  @service declare cable: CableService;
  @service('slr-loan-standing-verification')
  declare slrLoanStandingVerification: SlrLoanStandingVerificationService;

  @tracked drawerState: DrawerState = 'idle';
  @tracked selectedFile: SelectedFile | null = null;
  @tracked errorMessage: string | null = null;
  @tracked realtimeState: SlrLoanStandingAuditState | null = null;

  private subscriptionParams: object | null = null;

  constructor(owner: Owner, args: LoanStandingUploadDrawerSignature['Args']) {
    super(owner, args);
    registerDestructor(this, () => this.unsubscribe());
  }

  get currentState() {
    return this.realtimeState ?? this.args.state;
  }

  get presentation() {
    return presentationForSlrLoanStanding(this.currentState);
  }

  get employeePlanAccountId() {
    return this.currentState.employeePlanAccountId;
  }

  get hasUploadAction() {
    return ['upload_document', 'upload_newer_document'].includes(this.currentState.action);
  }

  get needsNameConfirmation() {
    return this.currentState.action === 'confirm_name_match';
  }

  get canSubmitDocument() {
    return !!this.selectedFile && !!this.employeePlanAccountId && !this.isBusy;
  }

  get isBusy() {
    return (
      this.drawerState === 'processing' ||
      this.submitDocument.isRunning ||
      this.confirmNameMatch.isRunning
    );
  }

  get isFileUploaderDisabled() {
    return this.isBusy || !!this.selectedFile;
  }

  private channelParamsFor = (asset?: DigitalAssetDetailSignature) => {
    if (asset?.uuid) {
      return { channel: 'ServiceResponseChannel', request_id: asset.uuid };
    }

    return null;
  };

  private subscribe = (asset?: DigitalAssetDetailSignature) => {
    const channelParams = this.channelParamsFor(asset);
    if (!channelParams) return;

    this.unsubscribe();
    this.subscriptionParams = channelParams;
    this.cable.subscribe(channelParams, {
      received: (data: unknown) => {
        this.handleCablePayload(data as SlrLoanStandingCablePayload);
      },
    });
  };

  private unsubscribe = () => {
    if (!this.subscriptionParams) return;
    this.cable.unsubscribe(this.subscriptionParams);
    this.subscriptionParams = null;
  };

  private handleCablePayload = (payload: SlrLoanStandingCablePayload) => {
    if (
      payload.employeePlanAccountId &&
      this.employeePlanAccountId &&
      payload.employeePlanAccountId !== this.employeePlanAccountId
    ) {
      return;
    }

    if (payload.status) {
      this.realtimeState = {
        ...this.currentState,
        status: payload.status,
        primaryReason: payload.primaryReason ?? this.currentState.primaryReason,
        reasons: payload.reasons ?? this.currentState.reasons,
        failures: payload.failures ?? this.currentState.failures,
        action: payload.action ?? this.currentState.action,
        sourceDocumentId: payload.sourceDocumentId ?? this.currentState.sourceDocumentId,
      };
    }

    if (payload.status === 'processing') {
      this.drawerState = 'processing';
      return;
    }

    if (payload.status) {
      this.applyAuditState(this.currentState);
      void this.refreshAfterStateChange();
    }
  };

  private applyAuditState = (state: SlrLoanStandingAuditState) => {
    this.realtimeState = state;
    if (state.status === 'processing') {
      this.drawerState = 'processing';
      return;
    }

    this.unsubscribe();
    this.drawerState = state.status === 'eligible' ? 'success' : 'error';
  };

  private refreshAfterStateChange = async () => {
    try {
      await this.args.onRefresh();
    } catch (error) {
      console.warn('[SLR loan standing] Failed to refresh current audit state', error);
    }
  };

  handleFileUploaded = (details: DigitalAssetDetailSignature) => {
    this.errorMessage = null;
    this.selectedFile = { name: details.filename, details };
    this.drawerState = 'file-selected';
  };

  removeFile = () => {
    this.selectedFile = null;
    this.errorMessage = null;
    this.drawerState = 'idle';
  };

  submitDocument = dropTask(async () => {
    if (!this.selectedFile || !this.employeePlanAccountId) return;

    this.errorMessage = null;
    this.drawerState = 'processing';
    this.subscribe(this.selectedFile.details);

    try {
      const state = await this.slrLoanStandingVerification.createVerificationDocument(
        this.employeePlanAccountId,
        this.selectedFile.details
      );
      this.applyAuditState(state);
      await this.refreshAfterStateChange();
    } catch (error) {
      console.error('[SLR loan standing] Failed to submit verification document', error);
      this.unsubscribe();
      this.errorMessage = 'slr.loan_standing.drawer.upload_error';
      this.drawerState = 'error';
    }
  });

  confirmNameMatch = dropTask(async (confirmed: boolean) => {
    if (!this.employeePlanAccountId) return;

    this.errorMessage = null;
    this.drawerState = 'processing';

    try {
      const state = await this.slrLoanStandingVerification.confirmNameMatch(
        this.employeePlanAccountId,
        confirmed
      );
      this.applyAuditState(state);
      await this.refreshAfterStateChange();
    } catch (error) {
      console.error('[SLR loan standing] Failed to confirm borrower name match', error);
      this.unsubscribe();
      this.errorMessage = 'slr.loan_standing.drawer.confirm_name_error';
      this.drawerState = 'error';
    }
  });

  reset = () => {
    this.unsubscribe();
    this.selectedFile = null;
    this.errorMessage = null;
    this.realtimeState = null;
    this.drawerState = 'idle';
  };

  handleClose = () => {
    this.reset();
    this.args.onClose();
  };

  <template>
    <Drawer
      @isOpen={{@isOpen}}
      @onClose={{this.handleClose}}
      @size="lg"
      @placement="right"
      data-test-slr-loan-standing-upload-drawer
      ...attributes
    >
      <:header>
        <h2>{{t "slr.loan_standing.drawer.title"}}</h2>
        <Text @variant="muted" @size="sm">{{t "slr.loan_standing.drawer.subtitle"}}</Text>
      </:header>

      <:body>
        <div class="tio-col-flush gap-5">
          {{#if (eq this.drawerState "success")}}
            <div class="rounded-xl border tio-border-success tio-bg-success-soft p-4">
              <div class="tio-row-top">
                <PhCheckCircle @size={{24}} class="tio-fg-success shrink-0" />
                <div class="tio-col-extra-tight">
                  <h3 class="tio-text-base-semibold tio-fg-success">
                    {{t "slr.loan_standing.title.eligible"}}
                  </h3>
                  <Text @size="sm">{{t "slr.loan_standing.description.eligible"}}</Text>
                </div>
              </div>
            </div>
          {{else}}
            <div class="rounded-xl border tio-border-default tio-bg-surface p-4">
              <div class="tio-row-top">
                <PhInfo @size={{20}} class="tio-fg-info shrink-0 mt-0.5" />
                <div class="tio-col-extra-tight">
                  <h3 class="tio-text-base-semibold tio-fg-primary">
                    {{t this.presentation.titleKey}}
                  </h3>
                  <Text @size="sm">{{t this.presentation.descriptionKey}}</Text>
                </div>
              </div>
            </div>

            {{#if this.needsNameConfirmation}}
              <div class="tio-col rounded-xl border tio-border-default p-4">
                <Text @size="sm">{{t "slr.loan_standing.drawer.confirm_name_prompt"}}</Text>
                <div class="tio-flex-tight">
                  <Button
                    @intent="primary"
                    @isRunning={{this.confirmNameMatch.isRunning}}
                    @isDisabled={{this.isBusy}}
                    data-test-slr-loan-standing-confirm-name-yes-button
                    {{on "click" (fn this.confirmNameMatch.perform true)}}
                  >
                    {{t "true"}}
                  </Button>
                  <Button
                    @intent="default"
                    @appearance="outlined"
                    @isDisabled={{this.isBusy}}
                    data-test-slr-loan-standing-confirm-name-no-button
                    {{on "click" (fn this.confirmNameMatch.perform false)}}
                  >
                    {{t "false"}}
                  </Button>
                </div>
              </div>
            {{/if}}

            {{#if this.hasUploadAction}}
              <div class="tio-col">
                <div>
                  <h3 class="tio-text-base-semibold tio-fg-primary">
                    {{t "slr.loan_standing.drawer.upload_heading"}}
                  </h3>
                  <Text @variant="muted" @size="sm">
                    {{t "slr.loan_standing.drawer.upload_instructions"}}
                  </Text>
                </div>

                <FileUploader
                  @accept=".pdf"
                  @useDropzone={{true}}
                  @disabled={{this.isFileUploaderDisabled}}
                  @didUploadFile={{this.handleFileUploaded}}
                  data-test-slr-loan-standing-file-uploader
                />

                {{#if this.selectedFile}}
                  <div class="tio-row-tight rounded-lg border tio-border-default px-3 py-2">
                    <PhFile @size={{16}} class="tio-fg-muted shrink-0" />
                    <Text @size="sm" class="flex-1 truncate">{{this.selectedFile.name}}</Text>
                    <Button
                      @appearance="minimal"
                      @intent="danger"
                      @size="sm"
                      @ariaLabel={{t "slr.loan_standing.drawer.remove_file"}}
                      @isDisabled={{this.isBusy}}
                      data-test-slr-loan-standing-remove-file-button
                      {{on "click" this.removeFile}}
                    >
                      <PhXCircle @size={{16}} />
                    </Button>
                  </div>
                {{/if}}
              </div>
            {{/if}}

            {{#if (eq this.drawerState "processing")}}
              <div
                class="rounded-xl border tio-border-info tio-bg-info-soft p-4"
                data-test-slr-loan-standing-processing-message
              >
                <Text @size="sm">{{t "slr.loan_standing.drawer.processing"}}</Text>
              </div>
            {{/if}}

            {{#if this.errorMessage}}
              <div class="rounded-xl border tio-border-danger tio-bg-danger-soft p-4">
                <div class="tio-row-top-tight">
                  <PhWarning @size={{16}} class="tio-fg-danger shrink-0 mt-0.5" />
                  <Text @size="sm">{{t this.errorMessage}}</Text>
                </div>
              </div>
            {{/if}}
          {{/if}}
        </div>
      </:body>

      <:footer>
        <div class="tio-flex-end gap-2">
          <Button
            @appearance="outlined"
            @intent="default"
            @isDisabled={{this.isBusy}}
            data-test-slr-loan-standing-close-drawer-button
            {{on "click" this.handleClose}}
          >
            {{t "slr.loan_standing.drawer.close"}}
          </Button>
          {{#if this.hasUploadAction}}
            <Button
              @intent="primary"
              @isRunning={{this.submitDocument.isRunning}}
              @isDisabled={{not this.canSubmitDocument}}
              data-test-slr-loan-standing-submit-document-button
              {{on "click" this.submitDocument.perform}}
            >
              {{t "slr.loan_standing.drawer.submit"}}
            </Button>
          {{/if}}
        </div>
      </:footer>
    </Drawer>
  </template>
}
